import { LifeCycleWatcher } from '@blocksuite/affine/std';
import * as Y from 'yjs';

/**
 * Client-side diagram applier (the bullet-proof half of agent diagramming).
 *
 * An agent (Core API `/diagram/generate`) does NOT raw-Yjs-mint surface elements — that lands in the
 * server's `prop:elements.value` map, which an already-open editor's own value map can diverge from,
 * so it persists but never renders live. Instead the agent drops a diagram SPEC into the doc's flat
 * top-level `pipe:pending-diagrams` Y.Map (a name-keyed root map, merges cleanly like `blocks`). This
 * watcher runs IN the live edgeless editor, observes that map, and mints each spec into the surface
 * via the editor's own `store.transact` — so the write lands in the exact value map the surface model
 * observes: it renders with no reload and converges across replicas, exactly like a human edit.
 *
 * The element field set mirrors `services/diagram` `mint_into` (and the proven `pipe-edgeless-spike`)
 * byte-for-byte, so cold-load and live-mint produce identical CRDT. Idempotent: an element id already
 * present is skipped, and an applied spec's key is deleted (so reopen / co-peers don't re-mint).
 */
const PENDING_DIAGRAMS_MAP = 'pipe:pending-diagrams';

interface ShapeSpec {
  id: string;
  index: string;
  shape_type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  fill_color: string;
  stroke_color: string;
  seed: number;
}

interface ConnectorSpec {
  id: string;
  index: string;
  source: string;
  target: string;
  seed: number;
}

interface DiagramSpec {
  shapes?: ShapeSpec[];
  connectors?: ConnectorSpec[];
}

type ElementsMap = Y.Map<unknown>;

/** Minimal surface-model shape we depend on: its observed `prop:elements` inner Y.Map. */
interface SurfaceLike {
  elements: { getValue(): ElementsMap | undefined };
}

/** Minimal Store shape we depend on (avoids importing the whole editor type surface). */
interface StoreLike {
  spaceDoc: Y.Doc;
  transact(fn: () => void): void;
  getBlocksByFlavour(flavour: string): { model: unknown }[];
}

// BlockSuite serializes a shape's bounds as `[x,y,w,h]`; whole numbers stringify without a fraction,
// matching the Rust minter's `num()`. The spec JSON already gives us numbers.
const xywh = (x: number, y: number, w: number, h: number) => `[${x},${y},${w},${h}]`;

function applyDiagram(
  store: StoreLike,
  surface: SurfaceLike,
  pending: Y.Map<string>,
  diagramId: string,
  specJson: string
) {
  let spec: DiagramSpec;
  try {
    spec = JSON.parse(specJson) as DiagramSpec;
  } catch {
    // Malformed spec: drop it so it doesn't wedge the map.
    store.transact(() => pending.delete(diagramId));
    return;
  }

  const elements = surface.elements.getValue();
  if (!elements) {
    return;
  }

  store.transact(() => {
    for (const s of spec.shapes ?? []) {
      if (elements.has(s.id)) {
        continue; // idempotent
      }
      const m = new Y.Map<unknown>();
      m.set('type', 'shape');
      m.set('id', s.id);
      m.set('index', s.index);
      m.set('seed', s.seed);
      m.set('xywh', xywh(s.x, s.y, s.w, s.h));
      m.set('shapeType', s.shape_type);
      m.set('shapeStyle', 'General');
      m.set('strokeColor', s.stroke_color);
      m.set('strokeWidth', 2);
      m.set('strokeStyle', 'solid');
      m.set('fillColor', s.fill_color);
      m.set('filled', true);
      m.set('roughness', 1.4);
      m.set('rotate', 0);
      m.set('text', new Y.Text(s.label));
      m.set('color', s.stroke_color);
      // Setting into the surface's observed value map triggers SurfaceBlockModel's own
      // onElementsMapChange → builds the element model → renders.
      elements.set(s.id, m);
    }
    for (const c of spec.connectors ?? []) {
      if (elements.has(c.id)) {
        continue; // idempotent
      }
      const m = new Y.Map<unknown>();
      m.set('type', 'connector');
      m.set('id', c.id);
      m.set('index', c.index);
      m.set('seed', c.seed);
      m.set('mode', 1); // ConnectorMode.Orthogonal — the editor routes the path
      m.set('source', { id: c.source });
      m.set('target', { id: c.target });
      m.set('stroke', '#1e293b');
      m.set('strokeWidth', 2);
      m.set('strokeStyle', 'solid');
      m.set('frontEndpointStyle', 'None');
      m.set('rearEndpointStyle', 'Arrow');
      elements.set(c.id, m);
    }
    // Applied: remove the spec so a reopen / co-peer doesn't re-process it.
    pending.delete(diagramId);
  });
}

export class PipeDiagramApplier extends LifeCycleWatcher {
  static override readonly key = 'pipe-diagram-applier';

  private readonly disposables: (() => void)[] = [];

  override mounted() {
    const store = this.std.store as unknown as StoreLike;
    const surfaceBlock = store.getBlocksByFlavour('affine:surface')[0];
    const surface = surfaceBlock?.model as SurfaceLike | undefined;
    if (!surface) {
      return; // an edgeless doc has a surface once loaded; nothing to do otherwise
    }

    const pending = store.spaceDoc.getMap<string>(PENDING_DIAGRAMS_MAP);

    const process = (diagramId: string) => {
      const specJson = pending.get(diagramId);
      if (specJson) {
        applyDiagram(store, surface, pending, diagramId, specJson);
      }
    };

    const onPending = (event: Y.YMapEvent<string>) => {
      event.changes.keys.forEach((change, diagramId) => {
        if (change.action === 'add' || change.action === 'update') {
          process(diagramId);
        }
      });
    };

    pending.observe(onPending);
    this.disposables.push(() => pending.unobserve(onPending));

    // Process specs already present (e.g. pushed while the doc was closed → render on open).
    pending.forEach((_specJson, diagramId) => process(diagramId));
  }

  override unmounted() {
    this.disposables.forEach(dispose => dispose());
    this.disposables.length = 0;
  }
}
